/**
 * WCAG 2.4.3 / 2.1.2 fix: opening a drawer (cart, menu, search, wishlist,
 * login...) does not move keyboard focus into it, and Tab is not trapped
 * inside it while it is open. A keyboard user has to Tab through the
 * entire rest of the page before ever reaching the drawer's contents.
 *
 * This theme's drawers share one generic component: a container with
 * class "t4s-drawer" (mini cart confirmed at id="t4s-mini_cart"; other
 * drawers - menu, search, wishlist, login - use the same base class).
 * Checking the theme's own drawer.min.css confirms the ONLY signal that
 * actually controls a drawer's visibility is the aria-hidden attribute:
 * the CSS rule is literally ".t4s-drawer[aria-hidden=false]" - nothing
 * in the stylesheet's show/hide logic depends on any class. An earlier
 * version of this script additionally required a "t4s-dn" class to be
 * absent before treating a drawer as open, which does not match how the
 * theme's own CSS decides visibility and could prevent detection
 * entirely depending on whether/when that class is actually removed;
 * this version drops that requirement and goes solely by aria-hidden,
 * matching the CSS exactly.
 *
 * This script does not touch how drawers are opened or closed (that
 * stays exactly as the theme's own bundled JS does it); it only reacts
 * to a drawer's aria-hidden becoming "false" or "true":
 *
 * 1. On open: remembers what had focus (so it can be restored later),
 *    then moves focus into the drawer - to its close button if present
 *    ([data-drawer-close]), otherwise the first focusable element
 *    inside it, otherwise the drawer container itself.
 *
 * 2. While open: traps Tab/Shift+Tab so focus cycles within the
 *    drawer's own focusable elements instead of escaping back out to
 *    the page behind it.
 *
 * 3. On close: returns focus to whatever triggered the drawer (e.g. the
 *    "Add to cart" button).
 *
 * Detection uses a short polling loop rather than only a
 * MutationObserver, because the mini cart's contents are re-rendered via
 * AJAX on every cart update (Shopify's section-rendering API), which can
 * replace the very node an attribute observer was watching and silently
 * stop it from ever firing again. Polling the current DOM state directly
 * sidesteps that regardless of how any given drawer gets updated.
 */
(function () {
  "use strict";

  var DRAWER_SELECTOR = ".t4s-drawer";
  var POLL_MS = 200;
  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var lastFocused = null;
  var openDrawer = null;

  function isVisible(el) {
    return el.getAttribute("aria-hidden") === "false";
  }

  function getFocusable(container) {
    var nodes = container.querySelectorAll(FOCUSABLE_SELECTOR);
    return Array.prototype.filter.call(nodes, function (el) {
      return el.offsetParent !== null;
    });
  }

  function focusFirstIn(drawer) {
    var closeBtn = drawer.querySelector("[data-drawer-close]");
    if (closeBtn) {
      closeBtn.focus();
      return;
    }
    var focusable = getFocusable(drawer);
    if (focusable.length) {
      focusable[0].focus();
      return;
    }
    if (!drawer.hasAttribute("tabindex")) {
      drawer.setAttribute("tabindex", "-1");
    }
    drawer.focus();
  }

  function handleTrapKeydown(event) {
    if (!openDrawer || event.key !== "Tab") return;
    if (!document.body.contains(openDrawer) || !isVisible(openDrawer)) {
      openDrawer = null;
      return;
    }
    var focusable = getFocusable(openDrawer);
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onDrawerOpened(drawer) {
    if (openDrawer === drawer) return;
    openDrawer = drawer;
    lastFocused = document.activeElement;
    // Small delay so this doesn't fight with the drawer's own open
    // animation/transition, which can briefly leave elements unfocusable.
    setTimeout(function () {
      if (openDrawer === drawer && isVisible(drawer)) {
        focusFirstIn(drawer);
      }
    }, 60);
  }

  function onDrawerClosed(drawer) {
    if (openDrawer !== drawer) return;
    openDrawer = null;
    if (lastFocused && typeof lastFocused.focus === "function") {
      lastFocused.focus();
    }
    lastFocused = null;
  }

  function poll() {
    var drawers = document.querySelectorAll(DRAWER_SELECTOR);
    var anyVisible = false;
    for (var i = 0; i < drawers.length; i++) {
      var drawer = drawers[i];
      if (isVisible(drawer)) {
        anyVisible = true;
        if (openDrawer !== drawer) {
          onDrawerOpened(drawer);
        }
      }
    }
    if (!anyVisible && openDrawer) {
      onDrawerClosed(openDrawer);
    }
  }

  function start() {
    document.addEventListener("keydown", handleTrapKeydown, true);
    poll();
    setInterval(poll, POLL_MS);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
